import { useCurrentUser } from '@qse/api'
import { AppRoutesList, UserRoleEnum } from '@qse/constants'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import {
  checkAppAccess,
  detectCurrentApp,
  getDefaultRoute,
  getKeycloakToken,
  isKeycloakAuth,
  isPathMatch,
  normalizePath,
  storage,
} from '@qse/utils'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { authService } from './auth-service'

// Check if user is in the middle of a 2FA flow
const isIn2FAFlow = () => {
  const tempSession = storage.localStorage.getItem('temp_session')
  const tempEmail = storage.localStorage.getItem('temp_email')
  const totpSetupData = storage.localStorage.getItem('totp_setup_data')
  const path = location.pathname
  const isOn2FAPage = path === AppRoutesList.twoFAVerify || path === AppRoutesList.twoFA

  // 🚨 CASE 1: You're on 2FA page but have no temp data → shouldn't be in flow
  if (isOn2FAPage && !tempSession && !tempEmail && !totpSetupData) {
    return false
  }

  // ✅ CASE 2: You're on 2FA page AND have temp data → you're in flow
  if (
    (path === AppRoutesList.twoFAVerify && (tempSession || tempEmail)) ||
    (path === AppRoutesList.twoFA && (tempSession || tempEmail || totpSetupData))
  ) {
    return true
  }

  // Default fallback → not in 2FA flow
  return false
}

// Utility function to check tokens with retry mechanism
const checkTokensWithRetry = async (
  isIn2FAFlow: () => boolean,
  retries = 3,
  delay = 100,
  context = 'Unknown',
): Promise<boolean> => {
  // Local Keycloak (Cognito replacement): a stored direct-grant token == logged in.
  if (isKeycloakAuth()) {
    return !!(await getKeycloakToken())
  }

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const session = await fetchAuthSession()
      const hasToken = !!session.tokens?.accessToken
      const in2FAFlow = isIn2FAFlow()

      if (hasToken) {
        return true
      }

      // If no token and we're in 2FA flow, wait a bit longer
      if (in2FAFlow && attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay))
        delay *= 2 // Exponential backoff
        continue
      }

      // If not in 2FA flow or last attempt, return false
      if (!in2FAFlow || attempt === retries - 1) {
        return false
      }
    } catch (error) {
      console.error(`${context} - Error checking tokens (attempt ${attempt + 1}):`, error)

      if (attempt === retries - 1) {
        return false
      }

      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay))
      delay *= 2 // Exponential backoff
    }
  }

  return false
}

export const useAuthCheck = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, setAuthenticated } = useUserStore()
  const { setQuotaAdjustmentGracePeriodStatus, setShowQuotaAdjustmentBanner } =
    useBillingStatusStore()
  const [isChecking, setIsChecking] = useState(true)
  const [hasValidToken, setHasValidToken] = useState<boolean | null>(null)
  const [mfaSetupInitiated, setMfaSetupInitiated] = useState(false)

  // Check for valid Cognito tokens with retry mechanism
  const checkTokens = useCallback(
    async (retries = 3, delay = 100): Promise<boolean> => {
      const hasToken = await checkTokensWithRetry(isIn2FAFlow, retries, delay, 'useAuthCheck')

      setHasValidToken(hasToken)

      return hasToken
    },
    [isIn2FAFlow],
  )

  // Fetch user data when we have valid tokens
  const { data: user, isLoading: isLoadingUser, error: userError, refetch } = useCurrentUser()

  // Update user in store
  const updateUserInStore = useCallback(
    async (userData: any) => {
      if (userData) {
        // Check if user has access to current app
        if (!checkAppAccess(userData.userRole)) {
          console.log(`Access denied: User role ${userData.userRole} not allowed for current app`)
          setUser(null)
          setAuthenticated(false)

          return
        }

        setUser(userData)
        setAuthenticated(true)
      } else {
        setUser(null)
        setAuthenticated(false)
      }
    },
    [setUser, setAuthenticated],
  )

  // Handle authentication flow
  const handleAuthFlow = useCallback(async () => {
    // Skip token checking if user is in 2FA flow
    const in2FAFlow = isIn2FAFlow()

    if (in2FAFlow) {
      setIsChecking(false)

      return
    }

    const hasToken = await checkTokens()

    if (hasToken) {
      // We have valid tokens
      // Add a small delay to ensure 2FA verification has completed
      await new Promise(resolve => setTimeout(resolve, 200))

      // Trigger user data fetch when we have valid tokens and not in 2FA flow
      let fetchedUser = user

      try {
        const refetchResult = await refetch()

        if (refetchResult && refetchResult.data) {
          fetchedUser = refetchResult.data
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
      }

      // If we're on an auth route, redirect based on user role (but skip 2FA routes)
      const authRoutes = [
        AppRoutesList.login,
        AppRoutesList.register,
        AppRoutesList.forgotPassword,
        AppRoutesList.resetPassword,
        AppRoutesList.verification,
        AppRoutesList.confirmEmail,
        AppRoutesList.completeRegistration,
        AppRoutesList.newPassword,
        AppRoutesList.accountDeletion,
      ]

      const isOnAuthRoute =
        isPathMatch(location.pathname, authRoutes) ||
        location.pathname.startsWith('/check-invitation/') ||
        location.pathname.startsWith('/check-reseller-invitation/')

      const isOn2FARoute = isPathMatch(location.pathname, [
        AppRoutesList.twoFA,
        AppRoutesList.twoFAVerify,
      ])

      // Don't redirect from 2FA routes or register route - let MFA flow handle them, allow register for new accounts
      const isOnRegisterRoute =
        normalizePath(location.pathname) === normalizePath(AppRoutesList.register)
      const isOnAccountDeletionRoute =
        normalizePath(location.pathname) === normalizePath(AppRoutesList.accountDeletion)

      if (
        isOnAuthRoute &&
        fetchedUser &&
        !isOn2FARoute &&
        !isOnRegisterRoute &&
        !isOnAccountDeletionRoute
      ) {
        const { appType } = detectCurrentApp()
        const defaultRoute = getDefaultRoute(fetchedUser.userRole, appType)

        navigate(defaultRoute, { replace: true })
      }
      // Don't handle 2FA route redirects here - let the MFA effects handle them
      // This prevents conflicts between auth flow and MFA requirements
    } else {
      // No valid tokens
      updateUserInStore(null)

      // If we're not on an auth route, redirect to login
      const allAuthRoutes = [
        AppRoutesList.login,
        AppRoutesList.register,
        AppRoutesList.forgotPassword,
        AppRoutesList.resetPassword,
        AppRoutesList.verification,
        AppRoutesList.confirmEmail,
        AppRoutesList.completeRegistration,
        AppRoutesList.twoFA,
        AppRoutesList.twoFAVerify,
        AppRoutesList.newPassword,
        AppRoutesList.accountDeletion,
      ]

      const isOnAuthRoute =
        isPathMatch(location.pathname, allAuthRoutes) ||
        location.pathname.startsWith('/check-invitation/') ||
        location.pathname.startsWith('/check-reseller-invitation/')

      if (!isOnAuthRoute) {
        navigate(AppRoutesList.login, { replace: true })
      }
    }

    setIsChecking(false)
  }, [checkTokens, location.pathname, navigate, updateUserInStore, refetch, isIn2FAFlow, user])

  // Initial auth check
  useEffect(() => {
    handleAuthFlow()
  }, [])

  // Handle user data updates - only on initial load or when user data actually changes
  useEffect(() => {
    if (hasValidToken && user && !isIn2FAFlow()) {
      // Check if user has access to current app
      if (!checkAppAccess(user.userRole)) {
        console.log(`Access denied: User role ${user.userRole} not allowed for current app`)

        // Don't force logout, let the popup handle the redirect
        return
      }

      // SUPER_ADMIN users bypass billing restrictions completely
      if (user.userRole === UserRoleEnum.SUPER_ADMIN) {
        console.log('SUPER_ADMIN user bypassing billing restrictions')
        updateUserInStore(user)

        return
      }

      // Check if user is inactive and redirect to billing
      if (user.status === 'incomplete') {
        console.log('User is inactive, redirecting to billing page')
        updateUserInStore(user) // Still set the user data

        // Only redirect if not already on billing or success page
        const isBillingRoute = [AppRoutesList.billing, AppRoutesList.success].includes(
          location.pathname as any,
        )

        if (!isBillingRoute) {
          navigate(AppRoutesList.billing, { replace: true })
        }

        return
      }

      updateUserInStore(user)

      // Check if user needs MFA setup and is on a protected route
      if (user.mfaStatus === null) {
        const isOnProtectedRoute = ![
          AppRoutesList.login,
          AppRoutesList.register,
          AppRoutesList.forgotPassword,
          AppRoutesList.resetPassword,
          AppRoutesList.verification,
          AppRoutesList.confirmEmail,
          AppRoutesList.completeRegistration,
          AppRoutesList.newPassword,
          AppRoutesList.twoFA,
          AppRoutesList.twoFAVerify,
        ].includes(location.pathname as any)

        if (isOnProtectedRoute) {
          // The MFA status effect will handle the redirect
        }
      }
    }
  }, [hasValidToken, user, isIn2FAFlow]) // Removed location.pathname dependency

  // Unified MFA handling to prevent infinite loops
  useEffect(() => {
    // Don't run during initial loading or when user data is still loading.
    // Keycloak (local MVP) has no Cognito MFA flow, so skip it entirely.
    if (!hasValidToken || !user || isLoadingUser || isIn2FAFlow() || isKeycloakAuth()) return

    const handleMFAFlow = async () => {
      const session = await fetchAuthSession()
      const hasAccessToken = !!session.tokens?.accessToken
      const isOn2FARoute = [AppRoutesList.twoFA, AppRoutesList.twoFAVerify].includes(
        location.pathname as any,
      )

      // Check if MFA was already completed/skipped in this session
      const isMfaVerified = authService.isMfaVerified()
      const isMfaStepCompleted = authService.isMfaStepCompleted()

      console.log('🔍 MFA Flow Check:', {
        hasAccessToken,
        userMfaStatus: user.mfaStatus,
        isMfaVerified,
        isMfaStepCompleted,
        currentPath: location.pathname,
        isOn2FARoute,
      })

      // Case 1: User has access token and MFA is completed - don't redirect
      if (hasAccessToken && (isMfaVerified || isMfaStepCompleted)) {
        // User is properly authenticated, no MFA action needed
        if (isOn2FARoute) {
          // Redirect away from 2FA routes since user doesn't need them
          navigate(AppRoutesList.favorites, { replace: true })
        }

        return
      }

      // Case 2: User has MFA enabled but needs verification
      // if (user.mfaStatus === 'enabled' && !hasAccessToken && !isOn2FARoute) {
      //   console.log('MFA verification required - redirecting to 2FA verify page')
      //   navigate(AppRoutesList.twoFAVerify, { replace: true })
      //   return
      // }

      // Case 3: User needs MFA setup (only for new users during login flow)
      if (user.mfaStatus === null && !isMfaStepCompleted) {
        const isOnProtectedRoute = ![
          AppRoutesList.login,
          AppRoutesList.register,
          AppRoutesList.forgotPassword,
          AppRoutesList.resetPassword,
          AppRoutesList.verification,
          AppRoutesList.confirmEmail,
          AppRoutesList.completeRegistration,
          AppRoutesList.newPassword,
          AppRoutesList.twoFA,
          AppRoutesList.twoFAVerify,
        ].includes(location.pathname as any)

        const isOn2FASetupPage = location.pathname === AppRoutesList.twoFA

        // Only redirect if user came from login flow (has temp session data)
        const tempSession = storage.localStorage.getItem('temp_session')
        const tempEmail = storage.localStorage.getItem('temp_email')
        const isInLoginFlow = !!(tempSession || tempEmail)

        if (isOnProtectedRoute && !isOn2FASetupPage && !mfaSetupInitiated && isInLoginFlow) {
          console.log('MFA setup required from login flow - redirecting to 2FA setup page')
          setMfaSetupInitiated(true)

          try {
            const totpSetup = await authService.setupTOTP()

            navigate(AppRoutesList.twoFA, {
              replace: true,
              state: {
                isFirstLogin: false,
                totpSetup,
              },
            })
          } catch (error) {
            console.error('Error setting up TOTP:', error)
            navigate(AppRoutesList.twoFA, { replace: true })
          }
        } else if (isOn2FASetupPage && !mfaSetupInitiated) {
          setMfaSetupInitiated(true)
        }

        return
      }

      // Case 4: User is on 2FA route but doesn't need MFA
      if (isOn2FARoute && (user.mfaStatus === 'disabled' || isMfaStepCompleted)) {
        console.log('MFA not required - redirecting to dashboard')
        navigate(AppRoutesList.favorites, { replace: true })

        return
      }

      // Reset MFA setup flag when status is no longer null
      if (user.mfaStatus !== null && mfaSetupInitiated) {
        setMfaSetupInitiated(false)
      }
    }

    handleMFAFlow()
  }, [hasValidToken, user, isLoadingUser, location.pathname, mfaSetupInitiated, navigate])

  // Update billing status store when user data includes billing info
  useEffect(() => {
    if (user?.billingInfo?.quotaAdjustmentGracePeriodStatus) {
      const gracePeriodStatus = user.billingInfo.quotaAdjustmentGracePeriodStatus

      setQuotaAdjustmentGracePeriodStatus(gracePeriodStatus)

      // Show banner if grace period is active and user is tenant admin
      if (gracePeriodStatus.hasActiveGracePeriod && user.userRole === UserRoleEnum.TENANT_ADMIN) {
        setShowQuotaAdjustmentBanner(true)
      } else {
        setShowQuotaAdjustmentBanner(false)
      }
    } else {
      // Clear grace period status if not present
      setQuotaAdjustmentGracePeriodStatus(null)
      setShowQuotaAdjustmentBanner(false)
    }
  }, [user, setQuotaAdjustmentGracePeriodStatus, setShowQuotaAdjustmentBanner])
  // Manual refresh function
  const refreshAuth = useCallback(async () => {
    setIsChecking(true)
    await handleAuthFlow()
  }, [handleAuthFlow])

  // Manual trigger for 2FA completion
  const triggerAuthCheckAfter2FA = useCallback(async () => {
    setIsChecking(true)

    // Wait a bit for tokens to be available
    await new Promise(resolve => setTimeout(resolve, 500))

    const hasToken = await checkTokens()

    if (hasToken) {
      try {
        await refetch()
      } catch (error) {
        console.error('Error fetching user data after 2FA:', error)
      }
    }

    setIsChecking(false)
  }, [checkTokens, refetch])

  return {
    isChecking: isChecking || (hasValidToken && isLoadingUser),
    hasValidToken,
    user,
    error: userError,
    refreshAuth,
    triggerAuthCheckAfter2FA,
    refetch,
    mfaSetupInitiated,
  }
}
